import { useEffect } from 'react';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';
import { getTypeformUrl } from '@/utils/integration';

import { ProviderContainer } from '../../components/ProviderContainer';
import type { TypeformResponse } from '../../integrationRedirection/typeform';
import type { IntegrationItemProps } from '../../types';
import { useTypeform } from './useTypeform';

export const TypeformItem = ({ isConnector }: IntegrationItemProps) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    typeformWorkspace,
    disconnectTypeformMutation,
    invalidateTypeformQuery,
  } = useTypeform(!isConnector);

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<TypeformResponse | null>('typeform-redirect', null);

  const handleConnect = () => {
    if (!currWorkspace) return;
    window.open(getTypeformUrl(currWorkspace.alias));
  };

  const handleDisconnect = () => {
    disconnectTypeformMutation();
  };

  useEffect(() => {
    if (isConnector) return;
    if (!currWorkspace) return;
    if (!redirect) return;

    setRedirect(null);
    removeKey();
    invalidateTypeformQuery();
    fetchFeedbackSources(currWorkspace.alias);
  }, [
    currWorkspace,
    fetchFeedbackSources,
    invalidateTypeformQuery,
    isConnector,
    redirect,
    removeKey,
    setRedirect,
  ]);

  return (
    <ProviderContainer
      multiInfoEnabled={false}
      connected={!!typeformWorkspace}
      source={SOURCE_TYPE.TYPEFORM}
      description="Import survey as feedback into Syncly."
      connectedDisplay={typeformWorkspace?.user_info?.name || 'Typeform'}
      info={typeformWorkspace!}
      onDisconnect={handleDisconnect}
      onConnectClick={handleConnect}
      additionalActions={() => (
        <Flex>
          <Button variant="outlined" onClick={() => {}}>
            Survey form list
          </Button>
        </Flex>
      )}
    />
  );
};
